// @ts-check

/**
 * Card data taken from the ConfirmationToken `payment_method_preview` plus the purchaser name,
 * used to enrich the backend payload. Shared by every Stripe express wallet (Google Pay, Apple Pay).
 * @typedef {Object} StripeWalletInfo
 * @property {string} [brand] - Card brand (e.g. 'visa').
 * @property {string} [funding] - Card funding type (e.g. 'credit').
 * @property {number} [expMonth] - Card expiration month.
 * @property {number} [expYear] - Card expiration year.
 * @property {string} [last4] - Last 4 digits of the card.
 * @property {string} [country] - Card issuer country.
 * @property {string} [holderName] - Cardholder name (from the purchaser).
 */

/**
 * @typedef {Object} StripeWalletEnginePayload
 * @property {string} [stripeConfirmationToken] - Stripe ConfirmationToken ID created from the
 *   Express Checkout Element.
 */

/**
 * @typedef {Object} StripeWalletStrategy
 * @property {function(StripeWalletInfo, StripeWalletEnginePayload): Promise<Object>} createPayload - Create payment payload.
 */

/**
 * Factory for a Stripe express-wallet strategy. Google Pay and Apple Pay share the exact same
 * payload contract; the only difference is the nested data key (`google_pay_data` vs
 * `apple_pay_data`), so each concrete strategy is a thin wrapper that parameterizes this builder.
 *
 * The confirmation token travels both at the root as `payment_token` and nested under
 * `{walletDataKey}.stripe_confirmation_token` (per backend contract). The strategy also attaches
 * the card data from the ConfirmationToken `payment_method_preview` (brand, expiration, masked
 * number, country) plus the cardholder name. Fields missing from `walletInfo` are omitted.
 *
 * Unlike Adyen, the wallet token is a Stripe ConfirmationToken created on the client from the
 * Express Checkout Element; no browser_info is needed (Stripe.js handles 3DS device data).
 *
 * @param {Object} params
 * @param {string} params.walletDataKey - Backend key for the nested wallet block
 *   (e.g. 'google_pay_data', 'apple_pay_data').
 * @returns {StripeWalletStrategy}
 */
const createStripeWalletStrategy = ({ walletDataKey }) => {
  /**
   * Create a payload for a Stripe express wallet.
   * @param {StripeWalletInfo} walletInfo - Card/wallet info from the Express Checkout Element
   *   confirm event.
   * @param {StripeWalletEnginePayload} enginePayload - Engine-specific payload.
   * @returns {Promise<Object>} The payload formatted for the backend.
   */
  const createPayload = async (walletInfo = {}, { stripeConfirmationToken } = {}) => {
    if (!stripeConfirmationToken) {
      throw new Error('Missing Stripe confirmation token');
    }

    const { brand, expMonth, expYear, last4, country, holderName } = walletInfo;

    return {
      payment_token: stripeConfirmationToken,
      ...(country !== undefined && { country }),
      ...(brand !== undefined && { card_brand: brand }),
      ...(expMonth !== undefined && { card_exp_month: expMonth }),
      ...(expYear !== undefined && { card_exp_year: expYear }),
      ...(last4 !== undefined && { card_number: `XXXX XXXX XXXX ${last4}` }),
      ...(country !== undefined && { card_country: country }),
      ...(holderName !== undefined && { card_holder_name: holderName }),
      [walletDataKey]: {
        stripe_confirmation_token: stripeConfirmationToken,
      },
    };
  };

  return { createPayload };
};

export default createStripeWalletStrategy;
